import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

let activeQuickAddRequest = null;

export class QuickAddComponent extends Component {

  #abortController = null;

  #cachedContent = new Map();

  #cartUpdateAbortController = new AbortController();

  #busyButton = null;
  #requestGeneration = 0;

  get productPageUrl() {
    const productCard =   (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct =   (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  #getSelectedVariantId() {
    const productCard =   (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#cartUpdateAbortController.signal.aborted) {
      this.#cartUpdateAbortController = new AbortController();
    }
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#requestGeneration += 1;
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    if (activeQuickAddRequest?.owner === this) activeQuickAddRequest = null;
    this.#setBusy(null, false);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  #isSelectedVariantUnavailable() {
    const productCard =   (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker =   (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  #setBusy(button, busy) {
    if (busy && button) {
      this.#busyButton = button;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setAttribute('aria-busy', 'true');
      return;
    }

    this.#busyButton?.removeAttribute('aria-busy');
    if (this.#busyButton) this.#busyButton.disabled = false;
    this.#busyButton = null;
    this.removeAttribute('aria-busy');
  }

  #clearStatus() {
    const status = this.querySelector('[data-quick-add-status]');
    if (status) status.textContent = '';
  }

  #announceError() {
    const status = this.querySelector('[data-quick-add-status]');
    if (!status) return;
    status.textContent = this.dataset.errorMessage || 'Unable to load product options. Try again.';
  }

  handleClick = async (event) => {
    event.preventDefault();

    const trigger =
      event.currentTarget instanceof HTMLButtonElement
        ? event.currentTarget
        : event.target instanceof Element
          ? event.target.closest('button')
          : null;
    const currentUrl = this.productPageUrl;
    if (!currentUrl || !(trigger instanceof HTMLButtonElement)) {
      this.#announceError();
      return;
    }

    if (this.dataset.usesSellingPlans === 'true') {
      window.location.href = currentUrl;
      return;
    }

    activeQuickAddRequest?.controller.abort();
    this.#abortController?.abort();

    const controller = new AbortController();
    const requestGeneration = ++this.#requestGeneration;
    this.#abortController = controller;
    activeQuickAddRequest = { owner: this, controller };
    this.#clearStatus();
    this.#setBusy(trigger, true);

    try {

      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl, controller.signal);
        if (controller.signal.aborted || requestGeneration !== this.#requestGeneration) return;

        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) throw new Error('Quick-add product content was not found');

        productGrid =   (gridElement.cloneNode(true));
        this.#cachedContent.set(currentUrl, productGrid);
      }

      if (controller.signal.aborted || requestGeneration !== this.#requestGeneration) return;

      const freshContent =   (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      if (controller.signal.aborted || requestGeneration !== this.#requestGeneration) return;

      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal(trigger);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (controller.signal.aborted) return;

      console.warn('[quick-add] Unable to load product options:', error);
      this.#announceError();
    } finally {
      if (activeQuickAddRequest?.controller === controller) activeQuickAddRequest = null;
      if (requestGeneration === this.#requestGeneration) {
        this.#abortController = null;
        this.#setBusy(null, false);
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = (trigger) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.syncHeaderContext();
    dialogComponent.setProductTitle(this.dataset.productTitle || '');
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog(trigger);

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl, signal) {
    const response = await fetch(productPageUrl, { signal });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    return new DOMParser().parseFromString(responseText, 'text/html');
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  syncHeaderContext() {
    const header = document.getElementById('header-component');
    this.toggleAttribute('header-transparent', header?.hasAttribute('transparent') ?? false);
    this.toggleAttribute('header-sticky-active', header?.dataset.stickyState === 'active');
  }

  setProductTitle(productTitle) {
    const heading = this.querySelector('#quick-add-dialog-title');
    if (!heading) return;

    const prefix = this.dataset.dialogTitlePrefix || 'Choose';
    heading.textContent = productTitle ? `${prefix} ${productTitle}` : prefix;
  }

  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleProductSelect = (event) => {

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement =   (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink =   (this.querySelector('.view-product-title a'));
        const mobileProductTitle =   (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {

      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
